import { ViewerConfig } from "@bronte/viewer/src/main";
import { loadScript } from "@bronte/common";
import {
  AmbrosiaCoursewareItem,
  LearnerFeatureFlag,
  Theme,
  TOCItem,
  TOCMapItem,
  RouteTarget,
  AmbrosiaComponent,
  AmbrosiaMetadata,
  TOC_LEVELS,
} from "../interfaces";

function deepFreeze(o: any) {
  Object.values(o).forEach((v) => Object.isFrozen(v) || deepFreeze(v));
  return Object.freeze(o);
}

/**
 * Class to hold an instance of all global variables
 */
export class Globals {
  isForcedOffline: boolean;
  theme!: Theme;
  currentTarget!: RouteTarget;
  viewerConfig!: ViewerConfig;
  elementConfigs: {
    [elementId: string]: AmbrosiaComponent; // stores the config of currently loaded chapter, lesson, screen and child components.
  } = {};
  private _ambrosiaJson: AmbrosiaCoursewareItem;
  private _cohortId: string;
  private _tocMap: Map<string, TOCMapItem>;
  public get cohortId(): string {
    return this._cohortId;
  }
  public set cohortId(value: string) {
    if (this._cohortId === "") {
      this._cohortId = value;
    } else {
      throw new Error("Cohort ID is already set");
    }
  }
  private _deploymentId: string;
  public get deploymentId(): string {
    return this._deploymentId;
  }
  public set deploymentId(value: string) {
    if (this._deploymentId === "") {
      this._deploymentId = value;
    } else {
      throw new Error("Deployment ID is already set");
    }
  }

  public get ambrosiaJson(): AmbrosiaCoursewareItem {
    return this._ambrosiaJson;
  }
  public set ambrosiaJson(value: AmbrosiaCoursewareItem) {
    // We only want to set this once, when we initialize the package
    if (this._ambrosiaJson.$ambrosia === "") {
      this._ambrosiaJson = value;

      const { tocLevel } = JSON.parse(
        this._ambrosiaJson.exportMetadata.metadata,
      ) as AmbrosiaMetadata;

      const isTwoLevelTOC = tocLevel?.toLowerCase() === TOC_LEVELS.twoLevel.toLowerCase();

      /**
       * Generate a TOC Map from TOC data in Ambrosia.
       *
       * Map Format - Standard Lesson: {
       *  isChapter/isLesson/isScreen: boolean
       *  screens: [Array of first screen id]
       *  elementToRoute: first screen id
       * }
       * * Map Format - Stitched Lesson: {
       *  isChapter/isLesson/isScreen: boolean
       *  screens: [Array of all screen ids]
       *  elementToRoute: first lesson id
       * }
       */
      const tocMapBuilder = (chapters: TOCItem[]): void => {
        chapters.forEach((chapter: TOCItem) => {
          chapter.children?.forEach((lesson: TOCItem) => {
            const firstScreenId = lesson.children[0]?.id; // first screen id in this lesson
            let screenIDs: string[];

            // playOrder on lesson means it is a stitched lesson.
            if (lesson.playOrder) {
              /**
               * Create array of all screen ids.
               * This will be used to decrypt all the assets and screen data files when lesson is loaded.
               */
              screenIDs = lesson.children?.map((screen: TOCItem) => screen.id) || [];
            } else {
              screenIDs = [firstScreenId];
            }

            if (!this._tocMap.has(chapter.id)) {
              this._tocMap.set(chapter.id, {
                isChapter: true,
                elementToRoute: lesson.playOrder ? lesson.id : firstScreenId,
                screens: screenIDs,
              });
            }

            this._tocMap.set(lesson.id, {
              isLesson: true,
              elementToRoute: lesson.playOrder ? lesson.id : firstScreenId,
              screens: screenIDs,
            });

            lesson.children?.forEach((screen: TOCItem) => {
              this._tocMap.set(screen.id, {
                isScreen: true,
                elementToRoute: lesson.playOrder ? lesson.id : screen.id,
                screens: lesson.playOrder ? screenIDs : [screen.id],
              });
            });
          });
        });
      };

      const twoLevelTocMapBuilder = (chapters: TOCItem[]): void => {
        chapters.forEach((chapter: TOCItem) => {
          const screenIDs = chapter.children?.map((screen: TOCItem) => screen.id);

          if (!this._tocMap.has(chapter.id)) {
            this._tocMap.set(chapter.id, {
              isChapter: true,
              elementToRoute: screenIDs[0],
              screens: [screenIDs[0]],
            });
          }

          screenIDs.forEach((screenId) => {
            this._tocMap.set(screenId, {
              isScreen: true,
              elementToRoute: screenId,
              screens: [screenId],
            });
          });
        });
      };

      if (isTwoLevelTOC) {
        twoLevelTocMapBuilder(this._ambrosiaJson.toc?.children || []);
      } else {
        tocMapBuilder(this._ambrosiaJson.toc?.children || []);
      }

      // Recursively makes this._ambrosiaJson immutable, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze#examples
      deepFreeze(this._ambrosiaJson);
    }
  }

  private _packagePath: string;
  public get packagePath(): string {
    return this._packagePath;
  }
  public set packagePath(value: string) {
    if (this._packagePath === "") {
      this._packagePath = value;
    }
  }

  private _featureFlagObj: LearnerFeatureFlag[] = [];

  public get featureFlagData(): LearnerFeatureFlag[] {
    return this._featureFlagObj;
  }
  public set featureFlagData(data: LearnerFeatureFlag[]) {
    if (this._featureFlagObj.length === 0) {
      this._featureFlagObj = data;
    }
  }

  public get tocMap(): Map<string, TOCMapItem> {
    return this._tocMap;
  }

  constructor() {
    this.isForcedOffline = false;
    this._packagePath = "";
    this._cohortId = "";
    this._deploymentId = "";
    this._ambrosiaJson = {
      $ambrosia: "",
      $id: "",
      $projectId: "",
      $workspaceId: "",
      configSchemaVersion: "",
      config: {},
      toc: {
        id: "",
        type: "",
        title: "",
        children: [],
      },
      activityTheme: {
        bodyFont: "",
        titleFont: "",
        textColor: "",
        primaryColor: "",
        surfaceColor: "",
        backgroundColor: "",
        successColor: "",
        errorColor: "",
      },
      selectedThemePayload: {
        id: "",
        name: "",
        themeVariants: [
          {
            themeId: "",
            variantId: "",
            variantName: "",
            config: "",
          },
        ],
      },
      scenarios: [{}],
      pluginPayload: {
        pluginRepositoryPath: "",
        summary: {
          pluginId: "",
          name: "",
          description: "",
          type: "",
          latestVersion: "",
          subscriptionId: "",
          createdAt: "",
          updatedAt: "",
          tags: [],
          publishMode: "",
        },
      },
      exportMetadata: {
        exportId: "",
        ancestry: [],
        startedAt: "",
        completedAt: "",
        exportType: "",
        metadata: "",
        elementsExportedCount: 0,
      },
    };
    this._tocMap = new Map();
  }

  loadDataFile = (fileId: string, callback?: (elementConfig: AmbrosiaComponent) => void): void => {
    const elementConfig = this.elementConfigs[fileId];

    if (elementConfig) {
      callback?.(elementConfig);
      return;
    }

    loadScript(`${this.packagePath}/data/${fileId}.js`, (scriptTag: HTMLElement) => {
      this.elementConfigs[fileId] = window.interactiveObj as AmbrosiaComponent;
      delete window.interactiveObj;
      document.body.removeChild(scriptTag);

      callback?.(this.elementConfigs[fileId]);
    });
  };
}

export default new Globals();
